import type { LocalAiRuntime, SpokenLanguage, VoiceManifest } from '../types/LocalAiTypes';
import { recordLocalAiDuration } from '../models/ModelTelemetry';
import { workerModelConfig } from '../models/WorkerModelConfig';
import { WorkerClient } from '../workers/WorkerClient';
import type { SpeechAttempt } from './SpeechModelSelection';

export class SttManager {
  private client: WorkerClient | undefined;
  private initialization: Promise<void> | undefined;
  private index = 0;

  /**
   * @param attempts What to try, in order — see `speechAttempts`. The first entry is what this
   * device would rather use; anything after it is a real fallback whose files are already on
   * the device, not a hopeful second guess.
   * @param halfPrecision Whether the GPU compiles fp16 shaders — see `supportsHalfPrecision`.
   */
  public constructor(
    private readonly manifest: VoiceManifest,
    private readonly attempts: readonly SpeechAttempt[],
    private readonly halfPrecision: boolean,
  ) {
    if (attempts.length === 0) throw new Error('No speech model is available on this device.');
  }

  /** The checkpoint and backend that actually started, which is not always the preferred one. */
  public get active(): SpeechAttempt {
    return this.attempts[Math.min(this.index, this.attempts.length - 1)]!;
  }

  public get runtime(): LocalAiRuntime {
    return this.active.runtime;
  }

  /**
   * Transcribes, moving down the list if the backend gives way underneath us.
   *
   * A session that starts is not a session that runs. ONNX Runtime compiles its shaders lazily,
   * so a GPU missing a feature the model needs — fp16 being the usual one — reports nothing at
   * load and throws on the first inference instead. That failure arrives here rather than in
   * `initialize`, and the answer to it is the same list: tear the worker down and try the next
   * entry, which is on the CPU backend and has no such surprises left in it.
   */
  public async transcribe(audio: Float32Array, language: SpokenLanguage): Promise<string> {
    const started = performance.now();
    try {
      for (;;) {
        await this.initialize();
        const alternative = this.index + 1 < this.attempts.length;
        /*
         * The request hands the sample buffer to the worker, which detaches it here. A copy is
         * only worth its 1.3 MB while there is still something else to feed it to.
         */
        const samples = alternative ? audio.slice() : audio;
        try {
          return await this.client!.request<string>(
            { type: 'TRANSCRIBE', audio: samples, language },
            { transfer: [samples.buffer], timeoutMs: 180_000 },
          );
        } catch (error) {
          if (!alternative) throw error;
          this.dispose();
          this.index += 1;
        }
      }
    } finally {
      recordLocalAiDuration('stt', performance.now() - started);
    }
  }

  public dispose(): void {
    this.client?.dispose();
    this.client = undefined;
    this.initialization = undefined;
  }

  private initialize(): Promise<void> {
    if (this.initialization) return this.initialization;
    const started = performance.now();
    this.initialization = this.startFirstThatWorks()
      .finally(() => recordLocalAiDuration('modelLoad', performance.now() - started));
    return this.initialization;
  }

  /**
   * Walks the remaining attempts until one starts.
   *
   * The failure that matters here is the one nobody can predict from feature detection: WebGPU
   * is reported as present, the adapter is then refused by the driver or cannot find the memory
   * for a 537 MB model, and the session throws during setup. When that happens the error worth
   * showing is the *last* one — the state the device actually ended in — not the first.
   */
  private async startFirstThatWorks(): Promise<void> {
    let failure: unknown;
    while (this.index < this.attempts.length) {
      const attempt = this.attempts[this.index]!;
      try {
        await this.start(attempt);
        return;
      } catch (error) {
        failure = error;
        this.client?.dispose();
        this.client = undefined;
        this.index += 1;
      }
    }
    // Nothing started. Stay pointing at the last thing tried, so `active` keeps reporting the
    // state the device ended in rather than an index past the end of the list.
    this.index = this.attempts.length - 1;
    throw failure ?? new Error('Speech recognition could not start on this device.');
  }

  private async start(attempt: SpeechAttempt): Promise<void> {
    const client = new WorkerClient(new Worker(new URL('../workers/whisper.worker.ts', import.meta.url), { type: 'module' }));
    this.client = client;
    await client.request<void>({
      type: 'INIT',
      config: workerModelConfig(this.manifest, attempt.model, attempt.runtime, this.halfPrecision),
    }, { timeoutMs: 300_000 });
  }
}
